import { Box, Typography, Loader } from '@lucid/lego';
import Table from './Table/Table';
import type {
  Prospect,
  ProspectSearchFilters,
} from '../../../api/services/lupp/prospect/dto.prospect';
import styles from './Prospects.module.scss';

type Theme = 'dark' | 'light';
type Labels = Record<string, string> | undefined;

/**
 * @component ProspectsListView
 * Renders the prospects list: loading/error states, or the populated table.
 *
 * @param {Object} props - Component props
 * @param {Theme} props.theme - Color theme for the component
 * @param {Labels} props.labels - Localized labels from Contentful
 * @param {boolean} props.labelsLoading - Loading state for labels
 * @param {Error | null} props.labelsError - Error state for labels
 * @param {boolean} props.isLoading - Loading state for prospects data
 * @param {Error | null} props.error - Error state for prospects data
 * @param {boolean} props.isFetching - Fetching state for prospects data
 * @param {string} props.searchQuery - Current search query (unfiltered)
 * @param {string} props.debouncedSearch - Debounced search query
 * @param {Prospect[]} props.tableData - Prospects to display in table
 * @param {number} props.totalCount - Total count of prospects (for pagination)
 * @param {number} props.currentPage - Current page number
 * @param {number} props.pageSize - Number of items per page
 * @param {(page: number) => void} props.onPageChange - Handler for page change
 * @param {(pageSize: number) => void} props.onPageSizeChange - Handler for page size change
 * @param {(search: string) => void} props.onSearchChange - Handler for search change
 * @param {(prospectId?: string) => void} props.onCreateQuotation - Handler for creating quotation
 * @param {() => void} props.onCreateProspect - Handler for creating new prospect
 * @param {() => void} props.onViewAllTestDrives - Handler for navigating to the test drive bookings admin page
 * @param {string | undefined} props.sortColumn - Current sort column
 * @param {'asc' | 'desc' | undefined} props.sortDirection - Current sort direction
 * @param {(column: string, direction: 'asc' | 'desc') => void} props.onSort - Handler for sort change
 * @param {string | undefined} props.selectedProspectId - Currently selected prospect ID
 * @param {(prospectId: string | null) => void} props.onProspectSelect - Handler for prospect selection
 * @param {string} props.activeTab - Active tab ID
 * @param {(tabId: string) => void} props.onTabChange - Handler for tab change
 * @param {(quoteId: string) => void} props.onQuoteClick - Handler for quote click
 * @param {string} props.locale - Current locale
 *
 * @returns {React.ReactElement} The rendered prospects list view
 *
 * @example
 * <ProspectsListView
 *   theme="dark"
 *   labels={labels}
 *   labelsLoading={false}
 *   isLoading={false}
 *   tableData={prospects}
 *   {...otherProps}
 * />
 */

export type ProspectsListViewProps = {
  theme: Theme;
  labels: Labels;
  labelsLoading: boolean;
  labelsError: Error | null;
  isLoading: boolean;
  error: Error | null;
  isFetching: boolean;
  searchQuery: string;
  debouncedSearch: string;
  tableData: Prospect[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSearchChange: (search: string) => void;
  onCreateQuotation: (prospectId?: string) => void;
  onCreateProspect: () => void;
  onViewAllTestDrives: () => void;
  sortColumn: string | undefined;
  sortDirection: 'asc' | 'desc' | undefined;
  onSort: (column: string, direction: 'asc' | 'desc') => void;
  filters?: ProspectSearchFilters;
  onFiltersChange?: (filters: ProspectSearchFilters) => void;
  selectedProspectId: string | undefined;
  onProspectSelect: (prospectId: string | null) => void;
  activeTab: string;
  onTabChange: (tabId: string) => void;
  onQuoteClick: (quoteId: string) => void;
  locale: string;
};

const ProspectsListView: React.FC<ProspectsListViewProps> = ({
  theme,
  labels,
  labelsLoading,
  labelsError,
  isLoading,
  error,
  isFetching,
  searchQuery,
  debouncedSearch,
  tableData,
  totalCount,
  ...tableProps
}) => {
  if (labelsLoading || isLoading) {
    return (
      <Box>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {labels?.['title'] ?? 'Prospects'}
        </Typography>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {labels?.['description'] ?? 'View the status of existing prospects'}
        </Typography>
        <Box className={styles.loaderContainer}>
          <Loader size={48} theme={theme} />
        </Box>
      </Box>
    );
  }

  if (labelsError) {
    return (
      <Box>
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_labels}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error && !searchQuery && !debouncedSearch && !isFetching) {
    return (
      <Box>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {labels?.['title'] ?? 'Prospects'}
        </Typography>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {labels?.['description'] ?? 'View the status of existing prospects'}
        </Typography>
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_prospects}
          </Typography>
        </Box>
      </Box>
    );
  }

  const invertedTheme: Theme = theme === 'dark' ? 'light' : 'dark';

  return (
    <Box>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        {labels?.['title'] ?? 'Prospects'}
      </Typography>
      <Typography variant="body" size="medium" color="gray-3" theme={theme}>
        {labels?.['description'] ?? 'View the status of existing prospects'}
      </Typography>
      <Box pt={24} className={styles.loadingContainer}>
        {isFetching && (
          <Box className="fetchingOverlay">
            <Loader size={48} theme={invertedTheme} />
          </Box>
        )}
        <Table
          theme={theme}
          data={error ? [] : tableData}
          totalCount={error ? 0 : totalCount}
          searchQuery={searchQuery}
          labels={labels || {}}
          {...tableProps}
        />
      </Box>
    </Box>
  );
};

export default ProspectsListView;
